import { create } from 'zustand';
import { connectSocket, getSocket, subscribeSession, unsubscribeSession } from '../services/socket';
import {
  getSessions,
  loadSession,
  streamChat,
  getChatInfo,
  uploadDocument,
  announceMessage,
  patchSessionData,
  deleteSession,
  setCaseCategoryApi,
  streamPreview,
  streamRefine,
  finalizeDocument,
  setUploadedDocAsTemplate,
  deleteUploadedDoc as deleteUploadedDocApi,
  type SessionSummary,
  type SessionDetail,
  type ChatResponse,
  type PreviewComment,
  type GeneratedFile,
  type TokenUsage,
  type PlanItem,
} from '../services/api';

export interface Message {
  id: string;
  caseId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface UploadedDoc {
  filename: string;
  name?: string;
  status?: string;
}

export interface UploadItem {
  id: string;
  filename: string;
  size: number;
  status: 'queued' | 'uploading' | 'done' | 'error';
  step?: string;
  summary?: string;
  error?: string;
}

interface AppState {
  // Sessions list (sidebar)
  cases: SessionSummary[];
  activeCaseId: string | null;
  loadingCases: boolean;

  // Active session detail
  activeSession: SessionDetail | null;
  messages: Message[];
  isTyping: boolean;
  streamingContent: string;
  readyToGenerate: boolean;
  generatedFiles: GeneratedFile[];

  // Token usage
  contextSize: number | null;
  lastUsage: TokenUsage | null;
  fieldLabels: Record<string, string>;
  fetchChatInfo: () => Promise<void>;

  // Admin panel
  adminOpen: boolean;
  setAdminOpen: (open: boolean) => void;
  usersAdminOpen: boolean;
  setUsersAdminOpen: (open: boolean) => void;

  // Layout
  sidebarWidth: number;
  contextWidth: number;
  setSidebarWidth: (w: number) => void;
  setContextWidth: (w: number) => void;
  // Mobile drawers
  mobileNavOpen: boolean;
  mobileContextOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
  setMobileContextOpen: (open: boolean) => void;

  // Upload queue
  uploadQueue: UploadItem[];
  uploadFiles: (files: File[]) => Promise<void>;
  clearUploadQueue: () => void;

  // Right context panel — request to switch active tab from elsewhere (e.g. after upload completes)
  requestedContextTab: 'context' | 'docs' | 'plan' | 'participants' | 'timeline' | null;
  requestContextTab: (tab: 'context' | 'docs' | 'plan' | 'participants' | 'timeline' | null) => void;

  // Downloaded files tracking (per session)
  seenFiles: Record<string, string[]>; // sessionId -> [urls]
  markFileSeen: (sessionId: string, url: string) => void;

  // Preview
  previewOpen: boolean;
  previewBody: string;
  previewLoading: boolean;
  previewComments: PreviewComment[];
  previewTemplate: string | null;
  previewDocumentId: string | null;       // id of the plan item being previewed (null = standalone)
  activeTool: string | null;             // doc-writer MCP tool currently in use
  openPreview: (documentId?: string | null, sourceDocId?: string | null) => Promise<void>;
  toggleDocAsTemplate: (docId: string, useAsTemplate?: boolean) => Promise<void>;
  deleteUploadedDoc: (docId: string) => Promise<void>;
  closePreview: () => void;
  addPreviewComment: (text: string, comment: string) => void;
  removePreviewComment: (id: string) => void;
  refineWithComments: () => Promise<void>;
  finalizeFromPreview: () => Promise<void>;

  // Actions
  fetchSessions: () => Promise<void>;
  setActiveCaseId: (id: string) => Promise<void>;
  startNewCase: () => void;
  sendUserMessage: (text: string) => Promise<void>;
  uploadDoc: (file: File) => Promise<void>;
  generate: () => Promise<void>;
  updateField: (key: string, value: string | null) => Promise<void>;
  deleteCase: (id: string) => Promise<void>;
  setCaseCategory: (category: string) => Promise<void>;
}

const WELCOME: Message = {
  id: 'welcome',
  caseId: '',
  role: 'assistant',
  content:
    'Вітаю! Я — **Юрист.AI** — ваш цифровий юридичний асистент.\n\nОпишіть вашу ситуацію, і я допоможу підготувати необхідний документ: позов, скаргу, претензію чи заяву.',
  createdAt: new Date().toISOString(),
};

function makeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const LS_SIDEBAR_W = 'ui:sidebarWidth';
const LS_CONTEXT_W = 'ui:contextWidth';
const LS_SEEN_FILES = 'ui:seenFiles';
const LS_CTX_SIZE = 'ui:contextSize';
const LS_LAST_USAGE = 'ui:lastUsageBySession';
const LS_FIELD_LABELS = 'ui:fieldLabels';

function readNum(key: string, fallback: number, min: number, max: number) {
  if (typeof localStorage === 'undefined') return fallback;
  const v = parseInt(localStorage.getItem(key) || '', 10);
  if (Number.isFinite(v) && v >= min && v <= max) return v;
  return fallback;
}

function readSeenFiles(): Record<string, string[]> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LS_SEEN_FILES);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readContextSize(): number | null {
  if (typeof localStorage === 'undefined') return null;
  const v = parseInt(localStorage.getItem(LS_CTX_SIZE) || '', 10);
  return Number.isFinite(v) && v > 0 ? v : null;
}

function readFieldLabels(): Record<string, string> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LS_FIELD_LABELS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readUsageMap(): Record<string, TokenUsage> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LS_LAST_USAGE);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeUsage(sessionId: string, usage: TokenUsage | null) {
  if (typeof localStorage === 'undefined') return;
  try {
    const map = readUsageMap();
    if (usage) map[sessionId] = usage;
    else delete map[sessionId];
    localStorage.setItem(LS_LAST_USAGE, JSON.stringify(map));
  } catch {}
}

export const useAppStore = create<AppState>((set, get) => ({
  cases: [],
  activeCaseId: null,
  loadingCases: false,
  activeSession: null,
  messages: [WELCOME],
  isTyping: false,
  streamingContent: '',
  readyToGenerate: false,
  generatedFiles: [],
  contextSize: readContextSize(),
  lastUsage: null,
  fieldLabels: readFieldLabels(),
  adminOpen: false,
  usersAdminOpen: false,
  sidebarWidth: readNum(LS_SIDEBAR_W, 256, 180, 520),
  contextWidth: readNum(LS_CONTEXT_W, 320, 220, 640),
  seenFiles: readSeenFiles(),
  uploadQueue: [],
  requestedContextTab: null,
  mobileNavOpen: false,
  mobileContextOpen: false,
  previewOpen: false,
  previewBody: '',
  previewLoading: false,
  previewComments: [],
  previewTemplate: null,
  previewDocumentId: null,
  activeTool: null,

  setAdminOpen: (open: boolean) => set({ adminOpen: open }),
  setUsersAdminOpen: (open: boolean) => set({ usersAdminOpen: open }),

  fetchChatInfo: async () => {
    try {
      const info = await getChatInfo();
      const updates: Partial<AppState> = {};
      if (info.contextSize) {
        updates.contextSize = info.contextSize;
        if (typeof localStorage !== 'undefined') {
          try { localStorage.setItem(LS_CTX_SIZE, String(info.contextSize)); } catch {}
        }
      }
      if (info.fieldLabels && Object.keys(info.fieldLabels).length > 0) {
        updates.fieldLabels = info.fieldLabels;
        if (typeof localStorage !== 'undefined') {
          try { localStorage.setItem(LS_FIELD_LABELS, JSON.stringify(info.fieldLabels)); } catch {}
        }
      }
      if (Object.keys(updates).length > 0) set(updates);
    } catch (err) {
      console.error('chatInfo failed', err);
    }
  },

  setSidebarWidth: (w: number) => {
    const clamped = Math.max(180, Math.min(520, Math.round(w)));
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_SIDEBAR_W, String(clamped));
    set({ sidebarWidth: clamped });
  },
  setContextWidth: (w: number) => {
    const clamped = Math.max(220, Math.min(640, Math.round(w)));
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_CONTEXT_W, String(clamped));
    set({ contextWidth: clamped });
  },

  setMobileNavOpen: (open: boolean) => set({ mobileNavOpen: open, mobileContextOpen: open ? false : get().mobileContextOpen }),
  setMobileContextOpen: (open: boolean) => set({ mobileContextOpen: open, mobileNavOpen: open ? false : get().mobileNavOpen }),

  markFileSeen: (sessionId: string, url: string) => {
    set((s) => {
      const list = new Set(s.seenFiles[sessionId] || []);
      list.add(url);
      const next = { ...s.seenFiles, [sessionId]: Array.from(list) };
      if (typeof localStorage !== 'undefined') {
        try { localStorage.setItem(LS_SEEN_FILES, JSON.stringify(next)); } catch {}
      }
      return { seenFiles: next };
    });
  },

  openPreview: async (documentId?: string | null, sourceDocId?: string | null) => {
    const { activeCaseId, activeSession } = get();
    if (!activeCaseId) return;
    // If no documentId given, default to the first pending item in the plan (or null = standalone).
    let targetId = documentId ?? null;
    if (targetId === null && activeSession?.documentPlan && activeSession.documentPlan.length > 0) {
      targetId = activeSession.documentPlan.find((p) => p.status === 'pending')?.id || null;
    }
    set({
      previewOpen: true,
      previewLoading: true,
      previewComments: [],
      previewBody: '',
      previewTemplate: null,
      previewDocumentId: targetId,
      activeTool: null,
    });
    try {
      await streamPreview(activeCaseId, {
        onMeta: (meta) => set({ previewTemplate: meta.templateType ?? null }),
        onToolUse: (tool, _server, fallback) => set({ activeTool: fallback ? null : tool }),
        onChunk: (delta) => set((s) => ({ previewBody: s.previewBody + delta })),
        onDone: (body) => set({ previewBody: body, previewLoading: false, activeTool: null }),
        onError: (msg) => {
          throw new Error(msg);
        },
      }, targetId, sourceDocId ?? null);
      set({ previewLoading: false, activeTool: null });
    } catch (err: any) {
      set({ previewLoading: false });
      const errMsg: Message = {
        id: makeId(),
        caseId: activeCaseId,
        role: 'assistant',
        content: `❌ Помилка попереднього перегляду: ${err.message}`,
        createdAt: new Date().toISOString(),
      };
      set((s) => ({ messages: [...s.messages, errMsg], previewOpen: false }));
    }
  },

  toggleDocAsTemplate: async (docId: string, useAsTemplate?: boolean) => {
    const { activeCaseId, activeSession } = get();
    if (!activeCaseId || !activeSession) return;
    const current = activeSession.uploadedDocs.find((d) => d.id === docId);
    const next = typeof useAsTemplate === 'boolean' ? useAsTemplate : !current?.useAsTemplate;
    // Optimistic update
    set((s) => {
      if (!s.activeSession) return s;
      const updated = s.activeSession.uploadedDocs.map((d) =>
        d.id === docId
          ? { ...d, useAsTemplate: next }
          : (next ? { ...d, useAsTemplate: false } : d)
      );
      return { activeSession: { ...s.activeSession, uploadedDocs: updated } };
    });
    try {
      await setUploadedDocAsTemplate(activeCaseId, docId, next);
    } catch (err) {
      // revert on failure
      set((s) => {
        if (!s.activeSession) return s;
        const reverted = s.activeSession.uploadedDocs.map((d) =>
          d.id === docId ? { ...d, useAsTemplate: !next } : d
        );
        return { activeSession: { ...s.activeSession, uploadedDocs: reverted } };
      });
      console.error('toggleDocAsTemplate failed', err);
    }
  },

  deleteUploadedDoc: async (docId: string) => {
    const { activeCaseId, activeSession } = get();
    if (!activeCaseId || !activeSession) return;
    const prev = activeSession.uploadedDocs;
    // Optimistic remove
    set((s) => {
      if (!s.activeSession) return s;
      return {
        activeSession: {
          ...s.activeSession,
          uploadedDocs: s.activeSession.uploadedDocs.filter((d) => d.id !== docId),
        },
      };
    });
    try {
      await deleteUploadedDocApi(activeCaseId, docId);
    } catch (err) {
      // revert on failure
      set((s) => (s.activeSession ? { activeSession: { ...s.activeSession, uploadedDocs: prev } } : s));
      console.error('deleteUploadedDoc failed', err);
    }
  },

  closePreview: () => set({ previewOpen: false }),

  addPreviewComment: (text: string, comment: string) => {
    set((s) => ({
      previewComments: [
        ...s.previewComments,
        { id: makeId(), text: text.trim(), comment: comment.trim() },
      ],
    }));
  },

  removePreviewComment: (id: string) => {
    set((s) => ({ previewComments: s.previewComments.filter((c) => c.id !== id) }));
  },

  refineWithComments: async () => {
    const { activeCaseId, previewBody, previewComments } = get();
    if (!activeCaseId || previewComments.length === 0) return;
    const baseBody = previewBody;
    const baseComments = previewComments;
    // Reset visible body to show streaming progression of refined version.
    set({ previewLoading: true, previewBody: '' });
    try {
      await streamRefine(activeCaseId, baseBody, baseComments, {
        onChunk: (delta) => set((s) => ({ previewBody: s.previewBody + delta })),
        onDone: (body) => set({ previewBody: body }),
        onError: (msg) => { throw new Error(msg); },
      });
      set({ previewComments: [], previewLoading: false });
    } catch (err: any) {
      // Restore original body on failure
      set({ previewBody: baseBody, previewLoading: false });
      console.error('refine error', err);
    }
  },

  finalizeFromPreview: async () => {
    const { activeCaseId, previewBody, previewDocumentId, fetchSessions, openPreview } = get();
    if (!activeCaseId) return;
    set({ previewLoading: true });
    try {
      const res = await finalizeDocument(activeCaseId, previewBody, previewDocumentId);
      const normalizedFiles: GeneratedFile[] = (res.files || []).map((f) =>
        typeof f === 'string'
          ? { url: f, filename: f.split('/').pop() || 'document.docx' }
          : (f as GeneratedFile)
      );
      const newFile: GeneratedFile | null = res.file
        ? res.file
        : normalizedFiles[normalizedFiles.length - 1] ?? null;

      const resultMsg: Message = {
        id: makeId(),
        caseId: activeCaseId,
        role: 'assistant',
        content: res.message,
        createdAt: new Date().toISOString(),
      };

      // Update activeSession.documentPlan with the latest from server (if returned)
      const updatedPlan = res.documentPlan;

      set((s) => ({
        messages: [...s.messages, resultMsg],
        generatedFiles: normalizedFiles.length > 0 ? normalizedFiles : s.generatedFiles,
        activeSession: s.activeSession
          ? {
              ...s.activeSession,
              generatedFiles: normalizedFiles.length > 0
                ? normalizedFiles
                : (newFile ? [...s.activeSession.generatedFiles, newFile] : s.activeSession.generatedFiles),
              documentPlan: updatedPlan ?? s.activeSession.documentPlan,
              state: res.nextDocumentId ? 'GENERATION' : 'DELIVERY',
            }
          : null,
        previewOpen: false,
        previewLoading: false,
        previewBody: '',
        previewComments: [],
        previewDocumentId: null,
      }));
      await fetchSessions();

      // Auto-progress to next pending document in the plan
      if (res.nextDocumentId) {
        // Small delay so the success message lands in the chat first
        setTimeout(() => { openPreview(res.nextDocumentId!).catch(() => {}); }, 400);
      }
    } catch (err: any) {
      set({ previewLoading: false });
      const errMsg: Message = {
        id: makeId(),
        caseId: activeCaseId!,
        role: 'assistant',
        content: `❌ Помилка генерації: ${err.message}`,
        createdAt: new Date().toISOString(),
      };
      set((s) => ({ messages: [...s.messages, errMsg], previewOpen: false }));
    }
  },

  deleteCase: async (id: string) => {
    try {
      await deleteSession(id);
      writeUsage(id, null);
      set((s) => {
        const cases = s.cases.filter((c) => c.id !== id);
        const wasActive = s.activeCaseId === id;
        return {
          cases,
          activeCaseId: wasActive ? null : s.activeCaseId,
          activeSession: wasActive ? null : s.activeSession,
          messages: wasActive ? [WELCOME] : s.messages,
          readyToGenerate: wasActive ? false : s.readyToGenerate,
          generatedFiles: wasActive ? [] : s.generatedFiles,
          lastUsage: wasActive ? null : s.lastUsage,
        };
      });
    } catch (err: any) {
      console.error('deleteCase error', err);
      throw err;
    }
  },

  updateField: async (key: string, value: string | null) => {
    const { activeCaseId, activeSession } = get();
    if (!activeCaseId) return;
    // Optimistic
    const prev = activeSession?.collectedData?.[key];
    set((s) => ({
      activeSession: s.activeSession
        ? {
            ...s.activeSession,
            collectedData: { ...(s.activeSession.collectedData || {}), [key]: value ?? '' },
          }
        : null,
    }));
    try {
      const { collectedData } = await patchSessionData(activeCaseId, { [key]: value });
      set((s) => ({
        activeSession: s.activeSession ? { ...s.activeSession, collectedData } : null,
      }));
    } catch (err) {
      console.error('updateField error', err);
      // Revert
      set((s) => ({
        activeSession: s.activeSession
          ? {
              ...s.activeSession,
              collectedData: { ...(s.activeSession.collectedData || {}), [key]: prev ?? '' },
            }
          : null,
      }));
    }
  },

  setCaseCategory: async (category: string) => {
    const { activeCaseId, activeSession } = get();
    if (!activeCaseId) return;
    await setCaseCategoryApi(activeCaseId, category);
    if (activeSession) {
      set({ activeSession: { ...activeSession, category } });
    }
  },

  fetchSessions: async () => {
    set({ loadingCases: true });
    try {
      const list = await getSessions();
      set({ cases: list });
    } catch (e) {
      console.error('fetchSessions error', e);
    } finally {
      set({ loadingCases: false });
    }
  },

  setActiveCaseId: async (id: string) => {
    // Unsubscribe from previous session, subscribe to new one via WebSocket.
    const prev = get().activeCaseId;
    if (prev && prev !== id) unsubscribeSession(prev);
    subscribeSession(id);

    // Hydrate token gauge instantly from localStorage cache while server fetches
    const cachedUsage = readUsageMap()[id] || null;
    set({
      activeCaseId: id,
      isTyping: false,
      streamingContent: '',
      lastUsage: cachedUsage,
    });
    try {
      const session = await loadSession(id);
      const msgs: Message[] = (session.chatHistory || []).map((m, i) => ({
        id: String(i),
        caseId: id,
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.content,
        createdAt: m.createdAt || new Date().toISOString(),
      }));
      const serverUsage = (session as any).lastUsage as TokenUsage | null | undefined;
      if (serverUsage) writeUsage(id, serverUsage);
      set({
        activeSession: session,
        messages: msgs,
        readyToGenerate: session.state === 'GENERATION',
        generatedFiles: session.generatedFiles || [],
        lastUsage: serverUsage ?? cachedUsage,
      });
    } catch (e) {
      console.error('loadSession error', e);
    }
  },

  startNewCase: () => {
    const newId = makeId();
    const prev = get().activeCaseId;
    if (prev) unsubscribeSession(prev);
    subscribeSession(newId);
    set({
      activeCaseId: newId,
      activeSession: {
        id: newId,
        title: 'Нова справа',
        state: 'INTAKE',
        category: null,
        chatHistory: [],
        uploadedDocs: [],
        generatedFiles: [],
        collectedData: {},
        documentPlan: [],
      },
      messages: [],
      readyToGenerate: false,
      generatedFiles: [],
      streamingContent: '',
    });
  },

  sendUserMessage: async (text: string) => {
    const { activeCaseId, fetchSessions } = get();
    const caseId = activeCaseId ?? makeId();
    if (!get().activeCaseId) set({ activeCaseId: caseId });

    const userMsg: Message = {
      id: makeId(),
      caseId,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    const botMsgId = makeId();
    const botMsg: Message = {
      id: botMsgId,
      caseId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };
    set((s) => ({
      messages: [...s.messages, userMsg, botMsg],
      isTyping: true,
      streamingContent: '',
    }));

    // Mutable context lets onMessageBreak swap the active message slot mid-stream
    const ctx = { botMsgId, acc: '' };

    try {
      await streamChat(caseId, text, {
        onChunk: (delta) => {
          ctx.acc += delta;
          set((s) => ({
            messages: s.messages.map((m) => m.id === ctx.botMsgId ? { ...m, content: ctx.acc } : m),
          }));
        },
        onExtracted: (data) => {
          // Pre-extraction completed on server — update right panel immediately,
          // before the LLM response even starts streaming.
          set((s) => ({
            activeSession: s.activeSession
              ? { ...s.activeSession, collectedData: { ...(s.activeSession.collectedData || {}), ...data } }
              : s.activeSession,
          }));
        },
        onMessageBreak: () => {
          // Finalize current bot message, start a fresh one for the follow-up pass
          const prevId = ctx.botMsgId;
          const nextId = makeId();
          const snapshot = ctx.acc.trim();
          ctx.botMsgId = nextId;
          ctx.acc = '';
          set((s) => ({
            messages: [
              ...s.messages.map((m) => m.id === prevId ? { ...m, content: snapshot } : m),
              { id: nextId, caseId, role: 'assistant' as const, content: '', createdAt: new Date().toISOString() },
            ],
          }));
        },
        onUsage: (u) => {
          set((s) => {
            const prev = s.lastUsage || {};
            const prompt = (prev as any).prompt_tokens ?? null;
            const completion = u.completion_tokens ?? null;
            const next: TokenUsage = {
              prompt_tokens: prompt,
              completion_tokens: completion,
              total_tokens: (prompt || 0) + (completion || 0) || completion || prompt || null,
            };
            return { lastUsage: next };
          });
        },
        onFinal: (f) => {
          if (f.usage) writeUsage(caseId, f.usage);
          if (f.contextSize && typeof localStorage !== 'undefined') {
            try { localStorage.setItem(LS_CTX_SIZE, String(f.contextSize)); } catch {}
          }
          // Use accumulated second-pass text for the active message (may differ from f.message which is full combined)
          set((s) => ({
            messages: s.messages.map((m) =>
              m.id === ctx.botMsgId ? { ...m, content: ctx.acc.trim() || f.message } : m
            ),
            readyToGenerate: f.action === 'ready_to_generate' || f.state === 'GENERATION',
            lastUsage: f.usage || s.lastUsage,
            contextSize: f.contextSize ?? s.contextSize,
            activeSession: s.activeSession
              ? {
                  ...s.activeSession,
                  collectedData: f.collectedData ?? s.activeSession.collectedData,
                  documentPlan: f.documentPlan ?? s.activeSession.documentPlan,
                  templateFields: f.templateFields ?? s.activeSession.templateFields,
                  state: f.state ?? s.activeSession.state,
                }
              : (f.collectedData
                  ? ({
                      id: caseId,
                      title: f.title || 'Нова справа',
                      state: f.state || 'INTAKE',
                      chatHistory: [],
                      uploadedDocs: [],
                      generatedFiles: [],
                      collectedData: f.collectedData,
                      documentPlan: f.documentPlan ?? [],
                      templateFields: f.templateFields ?? [],
                      updatedAt: new Date().toISOString(),
                    } as SessionDetail)
                  : null),
          }));
          if (f.title) {
            set((s) => ({
              cases: s.cases.map((c) =>
                c.id === caseId ? { ...c, title: f.title!, updatedAt: new Date().toISOString() } : c
              ),
            }));
          }
        },
        onError: (msg) => { throw new Error(msg); },
      });

      await fetchSessions();

      // Background extraction + plan run on server after SSE closes.
      // Reload session after a short delay to pick up the enriched data.
      setTimeout(async () => {
        if (get().activeCaseId === caseId) {
          try {
            const session = await loadSession(caseId);
            set({ activeSession: session });
          } catch {}
        }
      }, 2500);
    } catch (err: any) {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === ctx.botMsgId ? { ...m, content: `❌ Помилка: ${err.message}` } : m
        ),
      }));
    } finally {
      set({ isTyping: false });
    }
  },

  uploadDoc: async (file: File) => {
    await get().uploadFiles([file]);
  },

  uploadFiles: async (files: File[]) => {
    if (files.length === 0) return;
    const { activeCaseId } = get();
    const caseId = activeCaseId ?? makeId();
    if (!get().activeCaseId) set({ activeCaseId: caseId });

    const items: UploadItem[] = files.map((f) => ({
      id: makeId(),
      filename: f.name,
      size: f.size,
      status: 'queued',
    }));
    set((s) => ({ uploadQueue: [...s.uploadQueue, ...items] }));

    const results: { filename: string; summary: string; error?: string }[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const itemId = items[i].id;
      set((s) => ({
        uploadQueue: s.uploadQueue.map((it) => it.id === itemId ? { ...it, status: 'uploading' } : it),
      }));
      try {
        const res = await uploadDocument(caseId, file, (step) => {
          set((s) => ({
            uploadQueue: s.uploadQueue.map((it) =>
              it.id === itemId ? { ...it, step } : it
            ),
          }));
        });
        const summary = res.uploadedDoc?.summary || file.name;
        results.push({ filename: file.name, summary, error: res.error || undefined });
        set((s) => ({
          uploadQueue: s.uploadQueue.map((it) =>
            it.id === itemId ? { ...it, status: 'done', summary } : it
          ),
          // Live-update right panel as each document fills new fields.
          activeSession: s.activeSession && res.collectedData
            ? { ...s.activeSession, collectedData: res.collectedData }
            : s.activeSession,
        }));
      } catch (err: any) {
        const msg = err?.message || 'upload failed';
        results.push({ filename: file.name, summary: file.name, error: msg });
        set((s) => ({
          uploadQueue: s.uploadQueue.map((it) =>
            it.id === itemId ? { ...it, status: 'error', error: msg } : it
          ),
        }));
      }
    }

    // Build batch summary
    const lines: string[] = [];
    const successful = results.filter((r) => !r.error);
    const failed = results.filter((r) => r.error);
    if (successful.length > 0) {
      lines.push(
        successful.length === 1
          ? 'Завантажено документ:'
          : `Завантажено документів: ${successful.length}`
      );
      successful.forEach((r, i) => lines.push(`${i + 1}) ${r.summary}`));
    }
    if (failed.length > 0) {
      lines.push('');
      lines.push(`Не вдалося обробити: ${failed.length}`);
      failed.forEach((r) => lines.push(`• ${r.filename} — ${r.error}`));
    }
    const announcement = lines.join('\n');

    // Local message
    const botMsg: Message = {
      id: makeId(),
      caseId,
      role: 'assistant',
      content: announcement || '✅ Готово',
      createdAt: new Date().toISOString(),
    };
    set((s) => ({ messages: [...s.messages, botMsg] }));

    // Persist on server (also re-runs extractor → updates collectedData)
    try {
      const res = await announceMessage(caseId, announcement);
      if (res.collectedData) {
        set((s) => ({
          activeSession: s.activeSession
            ? { ...s.activeSession, collectedData: res.collectedData! }
            : s.activeSession,
        }));
      }
    } catch (err) {
      console.error('announce failed', err);
    }

    // Refresh session list and reload active session to pull uploadedDocs
    await get().fetchSessions();
    if (caseId) {
      try {
        const session = await loadSession(caseId);
        set((s) => ({
          activeSession: session,
        }));
      } catch {}
    }

    // When at least one file uploaded successfully, switch the right panel to "Файли"
    // and dismiss the upload window immediately. On all-fail, leave the queue visible
    // so the user can read the error and close it manually via the X button.
    if (successful.length > 0) {
      const ids = new Set(items.map((i) => i.id));
      set((s) => ({
        uploadQueue: s.uploadQueue.filter((it) => !ids.has(it.id)),
        requestedContextTab: 'docs',
      }));
    }
  },

  clearUploadQueue: () => set({ uploadQueue: [] }),
  requestContextTab: (tab) => set({ requestedContextTab: tab }),

  generate: async () => {
    // New flow: open preview first; user reviews and finalizes from there.
    await get().openPreview();
  },
}));

// ── WebSocket: real-time session data updates ────────────────────────────────
// Connect once and listen for server-pushed session:update events.
// Updates collectedData / documentPlan / templateFields / state without waiting
// for the SSE stream or a manual reload.
if (typeof window !== 'undefined') {
  connectSocket();
  getSocket().on('session:update', (payload: {
    collectedData?: Record<string, string>;
    documentPlan?: any[];
    templateFields?: any[];
    state?: string;
  }) => {
    useAppStore.setState((s) => {
      if (!s.activeSession) return s;
      return {
        activeSession: {
          ...s.activeSession,
          ...(payload.collectedData !== undefined && {
            collectedData: { ...(s.activeSession.collectedData || {}), ...payload.collectedData },
          }),
          ...(payload.documentPlan !== undefined && { documentPlan: payload.documentPlan }),
          ...(payload.templateFields !== undefined && { templateFields: payload.templateFields }),
          ...(payload.state !== undefined && { state: payload.state }),
        },
      };
    });
  });
}
